import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { createOTP, deleteOTP, useOTP } from '../entities/otp-tokens.js';
import { User } from '../entities/user.js';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { notifyOtp } from '../notification/otp.js';
import { mongo } from '../server/mongo.js';

function normalizeEmail(email: string): string {
  const emailLower = email.trim().toLowerCase();
  if (!emailRegexCheck(emailLower)) throw new ExpectedError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
  return emailLower;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function newestUserForEmail(emailLower: string) {
  const regex = { $regex: `^${escapeRegExp(emailLower)}$`, $options: 'i' };
  const users = await mongo()
    .collection<User>(User._collectionName)
    .find({ $or: [{ email: regex }, { username: regex }] })
    .toArray();
  users.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
  return users[0];
}

export const sendOTP = registerMutation({
  operationName: 'sendOTP',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'email', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  resolve: (userID?: string, email?: string, institutionID?: string) => {
    return async () => {
      if (!userID && !email) throw new Error('Needs userID or email, to send OTP');
      const suppliedEmailLower = email ? normalizeEmail(email) : undefined;
      const userCollection = mongo().collection<User>(User._collectionName);
      const user = userID
        ? await userCollection.findOne({ _id: userID })
        : await newestUserForEmail(suppliedEmailLower!);
      if (userID && !user) throw new UserNotFoundError(userID);

      const targetUserID = user?._id ?? null;
      const emailLower = userID ? normalizeEmail(user!.email) : suppliedEmailLower!;
      const code = await createOTP({ userID: targetUserID, emailLower });
      try {
        // The recipient has no session yet. Pending tickets use the e-mail-only delivery path.
        await runInContext(serverContext.sessionID, () =>
          notifyOtp(targetUserID, code, institutionID, targetUserID ? undefined : emailLower),
        );
      } catch {
        logger.error('otp delivery failed');
        await deleteOTP({ userID: targetUserID, emailLower, otp: code });
        return false;
      }
      return true;
    };
  },
});

export const validateOTP = registerMutation({
  operationName: 'validateOTP',
  type: () => String,
  params: [
    { name: 'otp', type: () => String },
    { name: 'email', type: () => String },
    { name: 'userID', type: () => String },
  ],
  resolve: (otp: string, email?: string, userID?: string) => {
    return async () => {
      if (!userID && !email) throw new ExpectedError('Bitte geben Sie eine E-Mail-Adresse an.');
      const emailLower = email ? normalizeEmail(email) : undefined;
      const emailOwner = !userID && emailLower ? await newestUserForEmail(emailLower) : undefined;
      const ticket = await useOTP(
        otp,
        userID ? { userID } : { emailLower, matchingUserID: emailOwner?._id },
      );

      let authenticatedUserID = ticket.userID;
      if (!authenticatedUserID) {
        // An account may have appeared after the code was sent. Its owner has the verified e-mail.
        let existingUser = await newestUserForEmail(ticket.emailLower);
        if (!existingUser) {
          try {
            authenticatedUserID = await runInContext(serverContext.sessionID, () =>
              createNewUser(
                new User({
                  email: ticket.emailLower,
                  username: ticket.emailLower,
                  firstName: ticket.emailLower.split('@')[0],
                  lastName: '-',
                  emailConfirmed: true,
                }),
              ),
            );
          } catch (error) {
            // If another request created the account, use that single account.
            existingUser = await newestUserForEmail(ticket.emailLower);
            if (!existingUser) throw error;
          }
        }
        authenticatedUserID ??= existingUser?._id ?? null;
      }
      if (!authenticatedUserID) throw new UserNotFoundError();

      await mongo()
        .collection<User>(User._collectionName)
        .updateOne({ _id: authenticatedUserID }, { $set: { emailConfirmed: true } });
      return generateLoginToken({ userID: authenticatedUserID, userClass: User._class, ttu: 1 });
    };
  },
});
